import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Plus,
  Users,
  Shield,
  UserPlus,
  Search,
  RefreshCw,
  MoreVertical,
  Mail,
  User,
  Trash2,
} from "lucide-react";
import { permissionsAPI } from "../services/api";
import { useApp } from "../contexts/AppContext";
import { useTheme } from "../contexts/ThemeContext";
import PageHeader from "../components/common/PageHeader";
import DataTable from "../components/common/DataTable/DataTable";
import { getInitials, getAvatarColor } from "../utils/avatarHelpers";
import GenericModal from "../components/ui/GenericModal";

const RolesPage = () => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const { isSuperAdmin, organizations, selectedOrganizationId } = useApp();

  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [roleUsers, setRoleUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const [isCreateRoleModalOpen, setIsCreateRoleModalOpen] = useState(false);
  const [isInviteUserModalOpen, setIsInviteUserModalOpen] = useState(false);
  
  const [newRoleName, setNewRoleName] = useState("");
  const [newUser, setNewUser] = useState({ email: "", name: "", surname: "", organizationIds: [] });

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const response = await permissionsAPI.getAllRoles();
      setRoles(response.data.data || []);
      if (response.data.data?.length > 0 && !selectedRole) {
        setSelectedRole(response.data.data[0].role);
      }
    } catch (err) {
      console.error("Error fetching roles:", err);
      setError(t("permissionsPage.messages.loadError"));
    } finally {
      setLoading(false);
    }
  };

  const fetchRoleUsers = async (roleName) => {
    try {
      setUsersLoading(true);
      const response = await permissionsAPI.getRoleUsers(roleName);
      setRoleUsers(response.data.data || []);
    } catch (err) {
      console.error("Error fetching role users:", err);
      setError(t("permissionsPage.messages.loadRoleError", { role: roleName }));
    } finally {
      setUsersLoading(false);
    }
  };

  useEffect(() => {
    if (isSuperAdmin()) {
      fetchRoles();
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    if (selectedRole) {
      fetchRoleUsers(selectedRole);
    }
  }, [selectedRole]);

  const handleCreateRole = async () => {
    if (!newRoleName.trim()) return;
    try {
      setActionLoading(true);
      await permissionsAPI.setRolePermissions(newRoleName.trim(), []);
      setSuccessMessage(t("permissionsPage.messages.saveSuccess", { role: newRoleName }));
      setNewRoleName("");
      setIsCreateRoleModalOpen(false);
      fetchRoles();
    } catch (err) {
      setError(err.response?.data?.message || t("permissionsPage.messages.saveError"));
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemoveUser = async (username) => {
    if (!window.confirm(t("rolesPage.deleteUserConfirm", { user: username }))) return;
    try {
      setActionLoading(true);
      await permissionsAPI.removeUserFromRole(selectedRole, username);
      setSuccessMessage(t("rolesPage.removeUserSuccess"));
      fetchRoleUsers(selectedRole);
    } catch (err) {
      setError(err.response?.data?.message || t("common.error"));
    } finally {
      setActionLoading(false);
    }
  };

  const handleInviteUser = async () => {
    if (!newUser.email || !newUser.name || !newUser.surname) return;
    try {
      setActionLoading(true);
      await permissionsAPI.addUserToRole(selectedRole, newUser);
      setSuccessMessage(t("rolesPage.inviteSuccess", { role: selectedRole }));
      setNewUser({ email: "", name: "", surname: "", organizationIds: [] });
      setIsInviteUserModalOpen(false);
      fetchRoleUsers(selectedRole);
    } catch (err) {
      setError(err.response?.data?.message || t("common.error"));
    } finally {
      setActionLoading(false);
    }
  };
  const toggleOrganization = (orgId) => {
    setNewUser(prev => {
      const ids = [...prev.organizationIds];
      const index = ids.indexOf(orgId);
      if (index === -1) {
        ids.push(orgId);
      } else {
        ids.splice(index, 1);
      }
      return { ...prev, organizationIds: ids };
    });
  };

  const handleDeleteRole = async (roleName) => {
    if (!window.confirm(t("rolesPage.deleteRoleConfirm", { role: roleName }))) return;
    
    try {
      setActionLoading(true);
      await permissionsAPI.deleteRole(roleName);
      setSuccessMessage(`Role ${roleName} deleted.`);
      if (selectedRole === roleName) setSelectedRole(null);
      fetchRoles();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete role");
    } finally {
      setActionLoading(false);
    }
  };

  const userColumns = [
    {
      key: "user",
      label: t("rolesPage.userName"),
      render: (user) => (
        <div className="flex items-center space-x-3">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold ${getAvatarColor(user.name || user.username)}`}>
            {getInitials(user.name || user.username)}
          </div>
          <div className="min-w-0">
            <div className={`text-sm font-medium ${colors.textPrimary} truncate`}>
              {user.name || user.username}
            </div>
            <div className={`text-xs ${colors.textTertiary} truncate`}>
              {user.email}
            </div>
          </div>
        </div>
      )
    },
    {
      key: "actions",
      label: "",
      render: (user) => (
        <div className="flex justify-end">
          <button
            onClick={() => handleRemoveUser(user.username)}
            className="p-2 rounded-lg text-red-500 hover:bg-red-500/10 transition-colors"
            title={t("rolesPage.removeUser")}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      )
    }
  ];

  if (!isSuperAdmin()) {
    return (
      <div className={`flex h-full items-center justify-center ${colors.bgPrimary}`}>
        <div className="text-center p-8 max-w-sm">
          <div className="w-20 h-20 bg-red-500/10 rounded-full mx-auto mb-6 flex items-center justify-center">
            <Shield className="w-10 h-10 text-red-500" />
          </div>
          <h3 className={`text-2xl font-bold ${colors.textPrimary} mb-3`}>{t("rolesPage.accessDenied")}</h3>
          <p className={colors.textTertiary}>{t("rolesPage.onlySuperAdmin")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-auto min-h-full lg:h-full px-2 sm:px-4 pb-4 space-y-4 overflow-y-auto lg:overflow-hidden custom-scrollbar">
      <div className="flex flex-col lg:flex-row h-auto lg:h-full gap-4 overflow-visible lg:overflow-hidden">
        {/* Left Side: Roles List */}
        <div className={`w-full lg:w-80 shrink-0 flex flex-col ${colors.cardBg} backdrop-blur-md ${colors.cardBorder} border rounded-[2rem] shadow-xl overflow-hidden min-h-[300px] lg:h-full`}>
          <div className="p-6 border-b border-white/5 flex items-center justify-between">
            <h2 className={`text-lg font-bold ${colors.textPrimary}`}>{t("rolesPage.roles")}</h2>
            <button 
              onClick={() => setIsCreateRoleModalOpen(true)}
              className="p-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white transition-colors"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-2 custom-scrollbar">
            {loading ? (
              <div className="flex justify-center py-8">
                <RefreshCw className="w-6 h-6 text-blue-500 animate-spin" />
              </div>
            ) : (
              roles.map((role) => {
                const isCoreRole = ['super_admin', 'organization_manager', 'trainer', 'student', 'manager'].includes(role.role.toLowerCase());
                return (
                  <div key={role.role} className="group relative">
                    <button
                      onClick={() => setSelectedRole(role.role)}
                      className={`w-full flex items-center space-x-3 p-3 rounded-2xl transition-all ${
                        selectedRole === role.role 
                          ? 'bg-blue-600 text-white shadow-lg' 
                          : `${colors.bgHover} ${colors.textSecondary}`
                      }`}
                    >
                      <Shield className={`w-4 h-4 ${selectedRole === role.role ? 'text-white' : 'text-blue-400'}`} />
                      <span className="font-medium truncate pr-8">{role.role}</span>
                    </button>
                    {!isCoreRole && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteRole(role.role);
                        }}
                        className={`absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity ${
                          selectedRole === role.role ? 'text-white hover:bg-white/10' : 'text-red-500 hover:bg-red-500/10'
                        }`}
                        title={t("rolesPage.deleteRole")}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Right Side: Role Users */}
        <div className={`flex-1 flex flex-col ${colors.cardBg} backdrop-blur-md ${colors.cardBorder} border rounded-[2rem] shadow-xl overflow-hidden min-h-[400px] lg:h-full`}>
          <div className="p-5 md:p-8 flex flex-col h-full">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 md:mb-8">
              <div>
                <h2 className={`text-xl lg:text-2xl font-bold ${colors.textPrimary} leading-tight`}>
                  {selectedRole ? `${t("rolesPage.usersWithRole")} "${selectedRole}"` : t("rolesPage.selectRole")}
                </h2>
                <p className={`text-xs lg:text-sm ${colors.textTertiary} mt-1`}>
                  {t("rolesPage.manageGroups")}
                </p>
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => selectedRole && fetchRoleUsers(selectedRole)}
                  className={`p-2.5 rounded-xl ${colors.bgHover} ${colors.textSecondary} border ${colors.border} transition-all`}
                  title={t("notificationsPage.refresh")}
                >
                  <RefreshCw className={`w-5 h-5 ${usersLoading ? 'animate-spin' : ''}`} />
                </button>
                <button
                  onClick={() => {
                    setIsInviteUserModalOpen(true);
                    if (selectedOrganizationId) {
                      setNewUser(prev => ({ ...prev, organizationIds: [selectedOrganizationId] }));
                    }
                  }}
                  disabled={!selectedRole}
                  className="flex-1 sm:flex-none flex items-center justify-center px-4 lg:px-6 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold transition-all shadow-lg shadow-blue-600/20 disabled:opacity-50 disabled:cursor-not-allowed text-sm whitespace-nowrap"
                >
                  <UserPlus className="w-4 h-4 lg:w-5 lg:h-5 mr-2" />
                  {t("rolesPage.inviteUser")}
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto lg:overflow-hidden custom-scrollbar">
               <DataTable
                data={roleUsers}
                columns={userColumns}
                loading={usersLoading}
                error={error}
                onClearError={() => setError(null)}
                emptyState={{
                  icon: Users,
                  title: t("notificationsPage.emptyState.title"),
                  message: selectedRole ? t("rolesPage.noUsers") : t("rolesPage.selectRole"),
                  action: selectedRole ? {
                    label: t("rolesPage.inviteUser"),
                    onClick: () => setIsInviteUserModalOpen(true),
                    icon: UserPlus,
                  } : null,
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Create Role Modal */}
      <GenericModal
        isOpen={isCreateRoleModalOpen}
        onClose={() => setIsCreateRoleModalOpen(false)}
        title={t("rolesPage.createRole")}
        footer={
          <div className="flex items-center space-x-3 w-full">
            <button
              onClick={() => setIsCreateRoleModalOpen(false)}
              className={`flex-1 py-3 rounded-xl font-bold ${colors.bgActive} ${colors.textSecondary} border ${colors.border}`}
            >
              {t("notificationsPage.createModal.cancel")}
            </button>
            <button
              onClick={handleCreateRole}
              disabled={actionLoading || !newRoleName}
              className="flex-1 py-3 rounded-xl font-bold bg-blue-600 text-white shadow-lg shadow-blue-500/20 disabled:opacity-50"
            >
              {actionLoading ? <RefreshCw className="w-5 h-5 animate-spin mx-auto" /> : t("rolesPage.createRole")}
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          <p className={`text-sm ${colors.textSecondary}`}>
            {t("rolesPage.manageGroups")}
          </p>
          <div className="space-y-2">
            <label className={`text-xs font-bold uppercase tracking-wider ${colors.textMuted}`}>{t("rolesPage.roleName")}</label>
            <input
              type="text"
              value={newRoleName}
              onChange={(e) => setNewRoleName(e.target.value)}
              placeholder="e.g. assistant_coach"
              className={`w-full px-4 py-3 rounded-xl ${colors.inputBg} ${colors.inputBorder} border outline-none focus:ring-2 focus:ring-blue-500/50 ${colors.inputText}`}
            />
          </div>
        </div>
      </GenericModal>

      {/* Invite User Modal */}
      <GenericModal
        isOpen={isInviteUserModalOpen}
        onClose={() => setIsInviteUserModalOpen(false)}
        title={`${t("rolesPage.inviteUser")} (${selectedRole})`}
        footer={
          <div className="flex items-center space-x-3 w-full">
            <button
              onClick={() => setIsInviteUserModalOpen(false)}
              className={`flex-1 py-3 rounded-xl font-bold ${colors.bgActive} ${colors.textSecondary} border ${colors.border}`}
            >
              {t("notificationsPage.createModal.cancel")}
            </button>
            <button
              onClick={handleInviteUser}
              disabled={actionLoading || !newUser.email || !newUser.name}
              className="flex-1 py-3 rounded-xl font-bold bg-blue-600 text-white shadow-lg shadow-blue-500/20 disabled:opacity-50"
            >
              {actionLoading ? <RefreshCw className="w-5 h-5 animate-spin mx-auto" /> : t("rolesPage.inviteUser")}
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className={`text-xs font-bold uppercase tracking-wider ${colors.textMuted}`}>{t("common.name")}</label>
              <input
                type="text"
                value={newUser.name}
                onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                placeholder="John"
                className={`w-full px-4 py-3 rounded-xl ${colors.inputBg} ${colors.inputBorder} border outline-none focus:ring-2 focus:ring-blue-500/50 ${colors.inputText}`}
              />
            </div>
            <div className="space-y-2">
              <label className={`text-xs font-bold uppercase tracking-wider ${colors.textMuted}`}>{t("common.surname")}</label>
              <input
                type="text"
                value={newUser.surname}
                onChange={(e) => setNewUser({...newUser, surname: e.target.value})}
                placeholder="Doe"
                className={`w-full px-4 py-3 rounded-xl ${colors.inputBg} ${colors.inputBorder} border outline-none focus:ring-2 focus:ring-blue-500/50 ${colors.inputText}`}
              />
            </div>
          </div>
          <div className="space-y-2">
            <label className={`text-xs font-bold uppercase tracking-wider ${colors.textMuted}`}>{t("rolesPage.email")}</label>
            <input
              type="email"
              value={newUser.email}
              onChange={(e) => setNewUser({...newUser, email: e.target.value})}
              placeholder="john@example.com"
              className={`w-full px-4 py-3 rounded-xl ${colors.inputBg} ${colors.inputBorder} border outline-none focus:ring-2 focus:ring-blue-500/50 ${colors.inputText}`}
            />
          </div>

          <div className="space-y-3">
            <label className={`text-xs font-bold uppercase tracking-wider ${colors.textMuted}`}>{t("notificationsPage.createModal.organization")}</label>
            <div className={`grid grid-cols-1 gap-2 max-h-48 overflow-y-auto p-3 rounded-xl border ${colors.inputBorder} ${colors.inputBg}`}>
              {/* Special 'All Organizations' Wildcard */}
              <label 
                className={`flex items-center p-2 rounded-lg transition-colors cursor-pointer border-b mb-1 ${
                  newUser.organizationIds.includes("all") ? 'bg-blue-600/20 text-blue-400 border-blue-500/30' : `${colors.bgHover} ${colors.textSecondary} border-transparent`
                }`}
              >
                <input
                  type="checkbox"
                  checked={newUser.organizationIds.includes("all")}
                  onChange={() => toggleOrganization("all")}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 mr-3"
                />
                <span className="text-sm font-bold">{t("notificationsPage.filters.all")} ({t("permissionsPage.allAccess")})</span>
              </label>

              {organizations.map((org) => {
                const isChecked = newUser.organizationIds.includes(org.id);
                // Disable individual orgs if 'all' is selected for clarity
                const isDisabled = newUser.organizationIds.includes("all");
                
                return (
                  <label 
                    key={org.id} 
                    className={`flex items-center p-2 rounded-lg transition-colors ${
                      isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                    } ${
                      isChecked ? 'bg-blue-600/10 text-blue-400' : `${colors.bgHover} ${colors.textSecondary}`
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      disabled={isDisabled}
                      onChange={() => toggleOrganization(org.id)}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 mr-3"
                    />
                    <span className="text-sm font-medium">{org.name}</span>
                  </label>
                );
              })}
            </div>
          </div>
        </div>
      </GenericModal>
    </div>
  );
};

export default RolesPage;
